import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import MessengerLayout from "../../layout/MessengerLayout";
import LeaveBalanceSummary from "../../components/leave/LeaveBalanceSummary";
import LeaveCalendar       from "../../components/leave/LeaveCalendar";
import LeaveHistoryTable   from "../../components/leave/LeaveHistoryTable";
import LeaveModals         from "../../components/leave/LeaveModals";
import { ErrorState }      from "../../components/ui/LoadingStates";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export default function LeaveMessengerPage() {
  const token = localStorage.getItem("token") || "";
  const location = useLocation();
  const navigate = useNavigate();

  const [balances,      setBalances]      = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError,  setBalanceError]  = useState(null);

  const [leaveHistory, setLeaveHistory]   = useState([]);
  const [histLoading,  setHistLoading]    = useState(true);
  const [histError,    setHistError]      = useState(null);

  const [calendarMonth, setCalendarMonth] = useState(dayjs());
  const [showForm,      setShowForm]      = useState(false);
  const [formData,      setFormData]      = useState({
    leave_type: "", start_date: "", end_date: "", reason: "", file: null,
  });

  const [blackouts,  setBlackouts]  = useState([]);
  const [teamMode,   setTeamMode]   = useState(false);
  const [teamLeaves, setTeamLeaves] = useState([]);
  const [canTeam,    setCanTeam]    = useState(false);

  // Manager / admin / executive can see the team toggle
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      const roles = String(u.roles || u.role || "").toLowerCase();
      setCanTeam(/manager|admin|executive/.test(roles));
    } catch { setCanTeam(false); }
  }, []);

  // Auto-open the apply modal when the dashboard quick-action links here
  // with ?apply=true. We then strip the param so refreshing the page
  // doesn't reopen the form on every reload.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("apply") === "true") {
      setShowForm(true);
      params.delete("apply");
      const qs = params.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const loadBlackouts = async () => {
    try {
      const start = calendarMonth.startOf("month").format("YYYY-MM-DD");
      const end   = calendarMonth.endOf("month").format("YYYY-MM-DD");
      const res = await fetch(`${API_BASE}/api/leave/blackouts?start=${start}&end=${end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return setBlackouts([]);
      const json = await res.json();
      setBlackouts(Array.isArray(json) ? json : []);
    } catch { setBlackouts([]); }
  };

  const loadTeamLeaves = async () => {
    if (!teamMode) return;
    try {
      const start = calendarMonth.startOf("month").format("YYYY-MM-DD");
      const end   = calendarMonth.endOf("month").format("YYYY-MM-DD");
      const res = await fetch(`${API_BASE}/api/leave/team/range?start=${start}&end=${end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return setTeamLeaves([]);
      const json = await res.json();
      setTeamLeaves(Array.isArray(json) ? json : []);
    } catch { setTeamLeaves([]); }
  };

  useEffect(() => {
    loadBlackouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth]);

  useEffect(() => {
    loadTeamLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth, teamMode]);

  const loadBalances = async () => {
    setBalanceLoading(true);
    setBalanceError(null);
    try {
      const res = await fetch(`${API_BASE}/api/leave/balances`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // Backend returns { balances: [...] } via LeaveBalancesOut.
      // Older payloads / proxies sometimes return a bare array — handle both.
      const list = Array.isArray(json)
        ? json
        : (Array.isArray(json?.balances) ? json.balances : []);
      setBalances(list);
    } catch (e) {
      setBalanceError(e.message);
    } finally {
      setBalanceLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistLoading(true);
    setHistError(null);
    try {
      const res = await fetch(`${API_BASE}/api/leave/history`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setLeaveHistory(Array.isArray(json) ? json : json?.records || []);
    } catch (e) {
      setHistError(e.message);
    } finally {
      setHistLoading(false);
    }
  };

  useEffect(() => {
    loadBalances();
    loadHistory();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["leave_apply", "leave_cancel", "leave_approve", "leave_reject"].includes(t)) {
        loadBalances();
        loadHistory();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => window.removeEventListener("kira_action_complete", handleKiraAction);
  }, []);

  const handleFormChange = (e) => {
    const { name, value, files, type } = e.target;
    setFormData(d => ({ ...d, [name]: type === "file" ? files?.[0] : value }));
  };

  const handleFormSubmit = async (e) => {
    e?.preventDefault?.();

    // Client-side validation — empty leave_type / start_date / end_date
    // would otherwise hit /api/leave/submit and be rejected with a 422
    // ("value is not a valid date"), confusing the user.
    const missing = [];
    if (!formData.leave_type) missing.push("leave type");
    if (!formData.start_date) missing.push("start date");
    if (!formData.end_date)   missing.push("end date");
    if (missing.length) {
      throw new Error(`Please fill in: ${missing.join(", ")}`);
    }
    if (formData.start_date && formData.end_date &&
        formData.end_date < formData.start_date) {
      throw new Error("End date must be on or after start date");
    }

    const body = new FormData();
    body.append("leave_type",  formData.leave_type);
    body.append("start_date",  formData.start_date);
    body.append("end_date",    formData.end_date);
    body.append("reason",      formData.reason || "");
    if (formData.file) body.append("file", formData.file);

    const res = await fetch(`${API_BASE}/api/leave/submit`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body,
    });
    if (!res.ok) throw new Error(await res.text());
    await loadHistory();
    await loadBalances();
    setFormData({ leave_type: "", start_date: "", end_date: "", reason: "", file: null });
  };

  const onModifyConfirmed = async (leave, patch) => {
    const res = await fetch(`${API_BASE}/api/leave/${leave.id}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(await res.text());
    await loadHistory();
  };

  const onCancelConfirmed = async (leave) => {
    const res = await fetch(`${API_BASE}/api/leave/${leave.id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(await res.text());
    await loadHistory();
    await loadBalances();
  };

  return (
    <MessengerLayout>
      <div className="p-6 space-y-5 text-white">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#00ff90]">Leave</h1>
            <p className="text-white/40 text-sm mt-0.5">Balances, calendar and history</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="text-sm px-4 py-2 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 transition-colors">
            + Apply for Leave
          </button>
        </div>

        {/* Balances */}
        <LeaveBalanceSummary
          balances={balances}
          loading={balanceLoading}
          error={balanceError}
          onRetry={loadBalances}
        />

        {/* Calendar + History — stacked full-width (calendar on top, history below) */}
        <div className="space-y-5">
          <div className="w-full">
            <LeaveCalendar
              calendarMonth={calendarMonth}
              setCalendarMonth={setCalendarMonth}
              leaveHistory={leaveHistory}
              loading={histLoading}
              setShowForm={setShowForm}
              setFormData={(f) => setFormData(prev => ({ ...prev, ...f }))}
              blackouts={blackouts}
              teamMode={teamMode}
              teamLeaves={teamLeaves}
              canToggleTeam={canTeam}
              onToggleTeam={setTeamMode}
            />
          </div>
          <div className="w-full">
            {histError ? (
              <ErrorState
                message="Couldn't load leave history"
                detail={histError}
                onRetry={loadHistory}
              />
            ) : (
              <LeaveHistoryTable
                leaveRecords={leaveHistory}
                loading={histLoading}
                onModifyConfirmed={onModifyConfirmed}
                onCancelConfirmed={onCancelConfirmed}
              />
            )}
          </div>
        </div>

      </div>

      <LeaveModals
        showForm={showForm}
        formData={formData}
        setShowForm={setShowForm}
        handleFormChange={handleFormChange}
        handleFormSubmit={handleFormSubmit}
      />
    </MessengerLayout>
  );
}